import React, { useEffect, useState } from 'react';
import { PixelGlyph } from '../common/PixelGlyph';
import { SunsetStripe } from '../common/SunsetStripe';

export const MinimalFooter: React.FC = () => {
  const [worldTimes, setWorldTimes] = useState({
    sf: '',
    paris: '',
    tokyo: '',
    moscow: '',
  });

  useEffect(() => {
    const updateTimes = () => {
      const now = new Date();
      setWorldTimes({
        sf: now.toLocaleTimeString('en-US', { timeZone: 'America/Los_Angeles', hour: '2-digit', minute: '2-digit' }),
        paris: now.toLocaleTimeString('en-US', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit' }),
        tokyo: now.toLocaleTimeString('en-US', { timeZone: 'Asia/Tokyo', hour: '2-digit', minute: '2-digit' }),
        moscow: now.toLocaleTimeString('en-US', { timeZone: 'Europe/Moscow', hour: '2-digit', minute: '2-digit' }),
      });
    };

    updateTimes();
    const interval = setInterval(updateTimes, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <footer className="relative bg-espresso-950 border-t border-white/10 pt-12 sm:pt-20 pb-8 sm:pb-12 px-4 sm:px-6 z-10 font-mono text-xs text-cream-muted">
      <div className="max-w-7xl mx-auto space-y-10 sm:space-y-16">
        {/* Top Row: Swiss World Clocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 pb-8 sm:pb-12 border-b border-white/10">
          <div>
            <div className="text-[10px] text-cream-dim tracking-wider uppercase mb-1">
              SAN FRANCISCO [PT]
            </div>
            <div className="text-sm font-bold text-cream-100">{worldTimes.sf || '00:00'}</div>
          </div>

          <div>
            <div className="text-[10px] text-cream-dim tracking-wider uppercase mb-1">
              PARIS [CET]
            </div>
            <div className="text-sm font-bold text-cream-100">{worldTimes.paris || '00:00'}</div>
          </div>

          <div>
            <div className="text-[10px] text-cream-dim tracking-wider uppercase mb-1">
              TOKYO [JST]
            </div>
            <div className="text-sm font-bold text-cream-100">{worldTimes.tokyo || '00:00'}</div>
          </div>

          <div>
            <div className="text-[10px] text-cream-dim tracking-wider uppercase mb-1">
              MOSCOW [MSK]
            </div>
            <div className="text-sm font-bold text-cream-100">{worldTimes.moscow || '00:00'}</div>
          </div>
        </div>

        {/* Middle Row: Links & Brand Architecture */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Brand Info */}
          <div className="md:col-span-5 space-y-4">
            <div className="flex items-center gap-2 text-cream-100 font-display font-extrabold text-xl tracking-wider">
              <span className="w-5 h-5 rounded bg-gradient-to-tr from-sunset-gold to-sunset-crimson flex items-center justify-center text-[11px] font-mono text-espresso-950 font-black">
                E
              </span>
              <span>EXAMPLE // SPECIMEN 01</span>
            </div>
            <p className="text-xs text-cream-dim max-w-sm leading-relaxed">
              Living proof of zero-latency fluid physics, Three.js WebGL choreography, and editorial restraint. Produced by TH157LE.
            </p>
            <div className="text-[11px] text-sunset-gold flex items-center gap-1.5 pt-2">
              <PixelGlyph name="pulse-dot" size={8} />
              <span>CLUSTER HOST: example.th157le.ru</span>
            </div>
          </div>

          {/* Navigation Links */}
          <div className="md:col-span-3 space-y-3">
            <div className="text-[10px] tracking-wider uppercase text-cream-dim mb-2">
              EXHIBITION
            </div>
            <ul className="space-y-2">
              <li>
                <a href="#hero" className="hover:text-cream-100 transition-colors">
                  01. Topological Hero Core
                </a>
              </li>
              <li>
                <a href="#manifesto" className="hover:text-cream-100 transition-colors">
                  02. Scrubbing Manifesto
                </a>
              </li>
              <li>
                <a href="#specimens" className="hover:text-cream-100 transition-colors">
                  03. Pinned Exhibits
                </a>
              </li>
              <li>
                <a href="#architecture" className="hover:text-cream-100 transition-colors">
                  04. Gapless Bento Grid
                </a>
              </li>
              <li>
                <a href="#playground" className="hover:text-cream-100 transition-colors">
                  05. 500+ Tok/s Playground
                </a>
              </li>
            </ul>
          </div>

          {/* Contact & Inquiries */}
          <div className="md:col-span-4 space-y-3">
            <div className="text-[10px] tracking-wider uppercase text-cream-dim mb-2">
              CONTACT & INQUIRIES
            </div>
            <ul className="space-y-2.5">
              <li>
                <a
                  href="https://t.me/TH157LECLAW"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-cream-100 transition-colors flex items-center justify-between group"
                >
                  <span className="text-cream-200 group-hover:text-sunset-gold transition-colors">Telegram (@TH157LECLAW)</span>
                  <PixelGlyph name="arrow-up-right" size={12} color="#d4af37" />
                </a>
              </li>
              <li>
                <a
                  href="mailto:support@th157le.ru"
                  className="hover:text-cream-100 transition-colors flex items-center justify-between group"
                >
                  <span className="text-cream-200 group-hover:text-sunset-gold transition-colors">Email (support@th157le.ru)</span>
                  <PixelGlyph name="arrow-up-right" size={12} color="#d4af37" />
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Line */}
        <div className="pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-[11px] text-cream-dim">
          <div>
            © {new Date().getFullYear()} TH157LE. ALL SYSTEM RIGHTS RESERVED.
          </div>
          <div className="flex items-center gap-4">
            <span>OKLCH WARM ESPRESSO</span>
            <span>•</span>
            <span>THREE.JS WEBGL2</span>
            <span>•</span>
            <span>LENIS 1.1</span>
          </div>
        </div>
      </div>

      <SunsetStripe className="mt-12 opacity-80" height="h-1.5" />
    </footer>
  );
};
